import React, { useState, useRef, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, MessageSquare, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { contactData } from '../data/mock';
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    service: 'general'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await addDoc(collection(db, "contact_inquiries"), {
        ...formData,
        createdAt: serverTimestamp(),
      });

      toast({
        title: "Message Sent Successfully!",
        description: "Thank you! We'll get back to you within 24 hours.",
      });

      setFormData({
        name: '',
        email: '',
        company: '',
        message: '',
        service: 'general'
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Failed to Send Message",
        description: "Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" ref={sectionRef} className="py-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-20 w-32 h-32 rounded-full opacity-5 animate-float" style={{
          background: `radial-gradient(circle, var(--accent-primary), transparent)`,
          animationDelay: '2s'
        }}></div>
        <div className="absolute bottom-32 left-16 w-28 h-28 rounded-full opacity-10 animate-float" style={{
          background: `radial-gradient(circle, var(--accent-purple), transparent)`,
          animationDelay: '4s'
        }}></div>
        
        {/* Floating contact icons */}
        <div className="absolute top-40 left-32 animate-float opacity-20" style={{ animationDelay: '1s' }}>
          <Mail className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
        </div>
        <div className="absolute bottom-40 right-32 animate-float opacity-15" style={{ animationDelay: '3s' }}>
          <Phone className="w-5 h-5" style={{ color: 'var(--accent-purple)' }} />
        </div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-6" style={{
            background: 'var(--accent-bg)',
            border: '1px solid var(--accent-primary)'
          }}>
            <MessageSquare className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
            <span className="body-sm font-medium" style={{ color: 'var(--accent-primary)' }}>
              Get In Touch
            </span>
          </div>
          <h2 className="display-md mb-6" style={{ color: 'var(--text-primary)' }}>
            Ready to Transform Your Business?
          </h2>
          <p className="body-lg max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Let's discuss how our automation solutions can help you save time, reduce costs, 
            and accelerate your business growth. Get in touch with our team today.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div className={`transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            <div className="p-8 rounded-2xl" style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-subtle)'
            }}>
              <h3 className="h2 mb-6" style={{ color: 'var(--text-primary)' }}>
                Send us a Message
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Input */}
                <div>
                  <label className="body-md block mb-2" style={{ color: 'var(--text-primary)' }}>
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full"
                    placeholder="John Doe"
                  />
                </div>

                {/* Email Input */}
                <div>
                  <label className="body-md block mb-2" style={{ color: 'var(--text-primary)' }}>
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full"
                    placeholder="john@company.com"
                  />
                </div>

                {/* Company Input */}
                <div>
                  <label className="body-md block mb-2" style={{ color: 'var(--text-primary)' }}>
                    Company
                  </label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full"
                    placeholder="Your Company Name"
                  />
                </div>

                {/* Service Interest */}
                <div>
                  <label className="body-md block mb-2" style={{ color: 'var(--text-primary)' }}>
                    Service Interest
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full p-3 rounded-xl border-2 transition-all duration-200 focus:outline-none"
                    style={{
                      background: 'var(--bg-secondary)',
                      borderColor: 'var(--border-primary)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <option value="general">General Inquiry</option>
                    <option value="business-automation">Business Process Automation</option>
                    <option value="devops">DevOps & Infrastructure</option>
                    <option value="cloud">Cloud Automation</option>
                    <option value="data">Data & Analytics</option>
                    <option value="custom">Custom Solution</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="body-md block mb-2" style={{ color: 'var(--text-primary)' }}>
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full"
                    placeholder="Tell us about your automation needs and goals..."
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full group ${isSubmitting ? 'opacity-50' : ''}`}
                  style={{
                    background: 'var(--accent-primary)',
                    color: 'var(--bg-primary)'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Contact Information */}
          <div className={`transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            <div className="space-y-8">
              {/* Contact Details */}
              <div className="p-8 rounded-2xl" style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-subtle)'
              }}>
                <h3 className="h2 mb-6" style={{ color: 'var(--text-primary)' }}>
                  Contact Information
                </h3>
                
                <div className="space-y-6">
                  {/* Email */}
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                      background: 'var(--accent-bg)'
                    }}>
                      <Mail className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
                    </div>
                    <div>
                      <p className="body-sm mb-1" style={{ color: 'var(--text-muted)' }}>
                        Email Us
                      </p>
                      <p className="body-md" style={{ color: 'var(--text-primary)' }}>
                        {contactData.email}
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                      background: 'var(--accent-bg)'
                    }}>
                      <Phone className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
                    </div>
                    <div>
                      <p className="body-sm mb-1" style={{ color: 'var(--text-muted)' }}>
                        Call Us
                      </p>
                      <p className="body-md" style={{ color: 'var(--text-primary)' }}>
                        {contactData.phone}
                      </p>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                      background: 'var(--accent-bg)'
                    }}>
                      <MapPin className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
                    </div>
                    <div>
                      <p className="body-sm mb-1" style={{ color: 'var(--text-muted)' }}>
                        Visit Us
                      </p>
                      <p className="body-md" style={{ color: 'var(--text-primary)' }}>
                        {contactData.address}
                      </p>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                      background: 'var(--accent-bg)'
                    }}>
                      <Clock className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
                    </div>
                    <div>
                      <p className="body-sm mb-1" style={{ color: 'var(--text-muted)' }}>
                        Business Hours
                      </p>
                      <p className="body-md" style={{ color: 'var(--text-primary)' }}>
                        {contactData.hours}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="p-8 rounded-2xl" style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-subtle)'
              }}>
                <h4 className="h3 mb-6" style={{ color: 'var(--text-primary)' }}>
                  Quick Actions
                </h4>
                
                <div className="space-y-4">
                  <Button className="w-full btn-secondary group">
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule a Demo
                  </Button>
                  
                  <Button className="w-full btn-secondary group">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Free Consultation
                  </Button>
                  
                  <Button className="w-full btn-secondary group">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Live Chat Support
                  </Button>
                </div>
              </div>

              {/* Response Time */}
              <div className="p-6 rounded-2xl text-center" style={{
                background: 'var(--accent-bg)',
                border: '1px solid var(--accent-primary)'
              }}>
                <CheckCircle className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--accent-primary)' }} />
                <p className="body-md mb-1" style={{ color: 'var(--text-primary)' }}>
                  Average Response Time
                </p>
                <p className="h3" style={{ color: 'var(--accent-primary)' }}>
                  &lt; 2 Hours
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};